import { faHeartRate as regHeartRate } from '@fortawesome/pro-regular-svg-icons';
import { faHeartRate as solidHeartRate } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ICorporationResponse,
    ILocationResponse,
    IModelAssignmentResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { TableColumn } from 'components/data_table/types';
import { GroupHierarchyText } from 'components/group_hierarchy/GroupHierarchyText';
import { BooleanStatusIcon } from 'components/icon/BooleanStatusIcon';
import { ActionMenuItem } from 'components/menu/ActionMenuItem';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { TablePageLayout } from 'components/page/styled';
import { CRUDTable } from 'components/table/CRUDTable';
import { useFeatureFlag } from 'configcat-react';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    AnalyticsModelsAssignmentFilter,
    usePagedGroupAnalyticsModelDataSource,
} from 'custom_hooks/data_hooks/data_source/analyticsModelAssignmentDataSource';
import { useUserDateTimeFormat } from 'custom_hooks/useUserDateTimeFormat';
import { AnalyticsModelAssignmentModal } from 'pages/Models/AssignedModalTab/AnalyticsModelAssignmentModal';
import { AssignedModelGroups } from 'pages/Models/AssignedModalTab/AssignedModelGroups';
import { AssignedModelParameters } from 'pages/Models/AssignedModalTab/AssignedModelParameters';
import { EditModelAssignmentModal } from 'pages/Models/AssignedModalTab/EditModelAssignmentModal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { tokens } from 'styles/tokens';
import { getId } from 'utils/data';
import { getSystemWorkbenchTab, WorkbenchTab } from 'utils/RouteHelper';

const tableQueryKey = 'analytics-group-model';
export type AssignedModelTabProps = Readonly<{
    location?: ILocationResponse;
    corporation?: ICorporationResponse;
    analyticsModelId?: string;
}>;
export function AssignedModelTab({
    corporation,
    location,
    analyticsModelId,
}: AssignedModelTabProps) {
    const { value: canCreateModelAssignments } = useFeatureFlag(
        'analyticsModelCreateAndEdit',
        true
    );

    const { showToast } = useNotificationContext();
    const history = useHistory();
    const { formatDateTime } = useUserDateTimeFormat();

    const dataSource = usePagedGroupAnalyticsModelDataSource({
        name: tableQueryKey,
        defaultFilter: {
            locationId: location?.Id,
            corporationId: corporation?.Id,
            nameSearch: undefined,
            analyticsModelId,
        },
        defaultSort: [
            {
                field: 'CreatedDate',
                dir: 'desc',
            },
        ],
    });

    const { setFilter, reloadPage, updateData } = dataSource;
    const updateRow = useCallback(
        (newRowData: IModelAssignmentResponse) => updateData([newRowData], getId),
        [updateData]
    );

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModelOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<IModelAssignmentResponse>();

    const getHierarchyPrefix = useCallback(
        (model: IModelAssignmentResponse) => {
            if (location && corporation) {
                return undefined;
            }
            if (corporation) {
                return model.Location.LocationName;
            }
            return `${model.Corporation.CorporationName} / ${model.Location.LocationName}`;
        },
        [corporation, location]
    );

    const getRootGroupComponent = useCallback(
        (rowData: IModelAssignmentResponse) => {
            const AnalyticsGroupRootHierarchy = rowData.AnalyticsGroupHierarchy.slice(
                0,
                rowData.AnalyticsGroupHierarchy.findIndex(
                    (hierarchyItem) =>
                        hierarchyItem.HierarchyId === rowData.RootGroup.RootGroupId
                )
            );

            const prefix = getHierarchyPrefix(rowData);

            return AnalyticsGroupRootHierarchy.length > 0 ? (
                <GroupHierarchyText
                    hierarchy={AnalyticsGroupRootHierarchy}
                    suffix={rowData.RootGroup.RootGroupName}
                    prefix={prefix}
                />
            ) : (
                <div>
                    {!(location && corporation) && `${prefix} / `}
                    <span className="text-nowrap bold">
                        {rowData.RootGroup.RootGroupName}
                    </span>
                </div>
            );
        },
        [corporation, getHierarchyPrefix, location]
    );

    const columns = useMemo<TableColumn<IModelAssignmentResponse>[]>(
        () => [
            {
                id: 'root-group',
                header: () => 'Root Group',
                value: ({ rowData }) => getRootGroupComponent(rowData),
                primary: true,
                locked: true,
            },
            {
                id: 'applied-model',
                header: () => 'Applied Model',
                value: ({ rowData }) => (
                    <div className="flex-row align-center">
                        <BooleanStatusIcon
                            className="pr-10"
                            status={!rowData.Model.AnalyticsModelIsDeprecated}
                            trueState={{ title: 'Approved' }}
                            falseState={{ title: 'Deprecated' }}
                        />
                        {rowData.Model.AnalyticsModelName}
                        {rowData.Model.AnalyticsModelIsGlobal ? ' (KCF)' : ''}
                    </div>
                ),
            },
            {
                id: 'version',
                header: () => 'Version',
                value: ({ rowData }) => rowData.Model.AnalyticsModelVersion,
            },
            {
                id: 'assigned-groups',
                header: () => 'Assigned Groups',
                value: ({ rowData }) => <AssignedModelGroups rowData={rowData} />,
            },
            {
                id: 'parameters',
                header: () => 'Parameters',
                value: ({ rowData }) => <AssignedModelParameters rowData={rowData} />,
            },
            {
                id: 'assigned-by',
                header: () => 'Assigned By',
                value: ({ rowData }) =>
                    `${rowData.CreatedBy.UserFirstName} ${rowData.CreatedBy.UserLastName}`,
            },
            {
                id: 'assigned-date',
                header: () => 'Assigned Date',
                value: ({ rowData }) => formatDateTime(rowData.CreatedDate),
                sortOn: 'CreatedDate',
            },
        ],
        [formatDateTime, getRootGroupComponent]
    );

    const navToTrendChart = (groupId: string) => async () => {
        // Hierarchy is not supported on the assigned model
        const treeItem = await sdClient.getTreeItem({ treeItemId: groupId });
        const systemId = treeItem.Hierarchy.find(
            (hierarchy) => hierarchy.HierarchyType === 'System'
        )?.HierarchyId;
        if (systemId) {
            history.push(getSystemWorkbenchTab(systemId, WorkbenchTab.Trend, groupId));
        } else {
            throw new Error('Group is missing its system');
        }
    };

    const onEdit = (assignment: IModelAssignmentResponse) => {
        setSelectedRecord(assignment);
        setEditModelOpen(true);
    };

    const customIcons = (assignment: IModelAssignmentResponse) => (
        <ActionMenuItem
            title="Go to Trend"
            icon={<FontAwesomeIcon icon={regHeartRate} color={tokens.text.secondary} />}
            hoverIcon={<FontAwesomeIcon icon={solidHeartRate} color={tokens.text.link} />}
            onSelect={navToTrendChart(assignment.AnalyticsGroup.GroupId)}
        />
    );

    const { mutate: deleteModelAssignment } = useMutation({
        mutationFn: async () => {
            if (selectedRecord) {
                await sdClient.deleteAnalyticsAssignment({
                    assignmentId: selectedRecord.Id,
                });
            }
        },

        onSuccess: () => {
            reloadPage();
            setDeleteOpen(false);
            setSelectedRecord(undefined);
            showToast({
                type: 'success',
                content: 'Successfully deleted model assignment',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to delete model assignment',
            }),
    });

    const onEditClose = (result?: IModelAssignmentResponse) => {
        if (result) {
            updateRow(result);
        }
        setEditModelOpen(false);
        setSelectedRecord(undefined);
    };

    useEffect(() => {
        setFilter((current) => ({
            ...current,
            locationId: location?.Id,
            corporationId: corporation?.Id,
        }));
    }, [corporation?.Id, location?.Id, setFilter]);

    return (
        <TablePageLayout>
            <CRUDTable<IModelAssignmentResponse, AnalyticsModelsAssignmentFilter>
                columns={columns}
                dataSource={dataSource}
                onEdit={canCreateModelAssignments ? onEdit : undefined}
                onCreate={
                    canCreateModelAssignments && !analyticsModelId
                        ? () => setCreateModalOpen(true)
                        : undefined
                }
                onDelete={(record) => {
                    setSelectedRecord(record);
                    setDeleteOpen(true);
                }}
                rowKey={(record) => `model-${record.Id}`}
                customActionItems={customIcons}
                noResultMessage="No assignments found."
            />
            {createModalOpen && (
                <AnalyticsModelAssignmentModal
                    corporation={corporation}
                    location={location}
                    onClose={() => setCreateModalOpen(false)}
                />
            )}
            {selectedRecord && editModalOpen && (
                <EditModelAssignmentModal
                    corporation={corporation}
                    location={location}
                    assignment={selectedRecord}
                    onClose={onEditClose}
                />
            )}
            {selectedRecord && deleteOpen && (
                <ConfirmModal
                    title={`Delete '${selectedRecord.AnalyticsGroup.GroupName}'`}
                    actionText="delete this model assignment? The data will no longer be available."
                    checkText="delete this model assignment and its data"
                    actionButtonText="Delete"
                    onAction={deleteModelAssignment}
                    onClose={() => setDeleteOpen(false)}
                />
            )}
        </TablePageLayout>
    );
}
